import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Eye, MessageSquare, Pencil, Save } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { SECTIONS, VERSION_KIND, REVIEW_STATUS } from "../../lib/planReview";
import { Card, FormRow } from "../onboarding/adminUi";
import { btn, fmtDate } from "../leads/PriorityBadge";

export const SectionsEditor = ({ r, locked, onSaved }) => {
  const [s, setS] = useState(r.sections || {});
  useEffect(() => setS(r.sections || {}), [r.sections]);
  const save = async () => {
    try { const { data } = await api.put(`/admin/plan-reviews/${r.id}/sections`, { sections: s }); toast.success(data.changed.length ? "Apartados guardados" : "Sin cambios"); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Card title="Borrador · apartados" testid="pr-sections-card" right={!locked && <button style={btn("#dc2626")} onClick={save} data-testid="pr-sections-save"><Save size={14} /> Guardar apartados</button>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {SECTIONS.map(([k, label]) => (
          <FormRow key={k} label={label}>
            <textarea className="qv-input" rows={4} readOnly={locked} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })}
              style={{ minHeight: 110, height: "auto", textTransform: "none", letterSpacing: 0, opacity: locked ? 0.7 : 1 }} data-testid={`pr-section-${k}`} />
          </FormRow>
        ))}
      </div>
    </Card>
  );
};

export const TrainingCard = ({ r, locked, onManual }) => {
  const p = r.plan;
  return (
    <Card title="Borrador · entrenamientos" testid="pr-training-card"
      right={p ? <Link to={`/app/admin/plans-v2/${p.id}`} style={btn(locked ? "transparent" : "#dc2626")} data-testid="pr-edit-training">{locked ? <><Eye size={14} /> Ver</> : <><Pencil size={14} /> Editar entrenamientos</>}</Link>
        : !locked && <button style={btn("#dc2626")} onClick={onManual} data-testid="pr-manual-draft">Crear borrador manual</button>}>
      {!p ? <div style={{ fontSize: 13, color: "#a3a3a3" }}>Todavía no hay borrador de entrenamientos.</div> : (
        <div style={{ fontSize: 13, color: "#d4d4d4" }} data-testid="pr-training-summary">
          <div style={{ fontWeight: 700 }}>{p.name} · v{p.version}</div>
          <div style={{ color: "#a3a3a3", marginTop: 4 }}>{(p.weeks || []).length} semanas · {p.day_count} días de entrenamiento</div>
          {(p.weeks || []).map((w, i) => (
            <div key={w.id || i} style={{ marginTop: 8 }}>
              <b>{w.label || `Semana ${i + 1}`}</b>: {(w.days || []).map((d) => d.title || d.day_type).join(" · ") || "sin días"}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export const CommentsCard = ({ r, onAdded }) => {
  const [text, setText] = useState("");
  const add = async () => {
    try { await api.post(`/admin/plan-reviews/${r.id}/comments`, { text }); setText(""); onAdded(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Card title="Comentarios internos · el cliente no los ve" testid="pr-comments-card">
      {(r.comments || []).slice().reverse().map((c) => (
        <div key={c.id} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "8px 0", fontSize: 13 }} data-testid={`pr-comment-${c.id}`}>
          <div style={{ color: "#737373", fontSize: 11 }}>{c.author} · {fmtDate(c.at)}</div>
          <div style={{ whiteSpace: "pre-wrap", color: "#e5e5e5" }}>{c.text}</div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <textarea className="qv-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Nota interna…" style={{ flex: 1, minHeight: 60, height: "auto" }} data-testid="pr-comment-input" />
        <button style={btn("transparent")} disabled={!text.trim()} onClick={add} data-testid="pr-comment-add"><MessageSquare size={14} /> Añadir</button>
      </div>
    </Card>
  );
};

const VersionViewer = ({ rid, vid, onClose }) => {
  const [v, setV] = useState(null);
  useEffect(() => { if (vid) api.get(`/admin/plan-reviews/${rid}/versions/${vid}`).then((x) => setV(x.data)).catch((e) => toast.error(formatErr(e))); }, [rid, vid]);
  return (
    <Dialog open={!!vid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ background: "#0a0a0a", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="pr-version-dialog">
        <DialogHeader><DialogTitle>Versión {v?.number}</DialogTitle></DialogHeader>
        {v && (<div style={{ fontSize: 13 }}>
          {SECTIONS.filter(([k]) => v.sections?.[k]).map(([k, l]) => <div key={k} style={{ marginBottom: 10 }}><b>{l}</b><div style={{ whiteSpace: "pre-wrap", color: "#d4d4d4" }}>{v.sections[k]}</div></div>)}
          {(v.weeks || []).map((w, i) => <div key={i}><b>{w.label || `Semana ${i + 1}`}</b>: {(w.days || []).map((d) => d.title || d.day_type).join(" · ")}</div>)}
        </div>)}
      </DialogContent>
    </Dialog>
  );
};

export const VersionsCard = ({ r, locked, onChange }) => {
  const [note, setNote] = useState("");
  const [view, setView] = useState(null);
  const save = async () => {
    try { await api.post(`/admin/plan-reviews/${r.id}/versions`, { note: note || null }); setNote(""); toast.success("Versión guardada"); onChange(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const restore = async (v) => {
    if (!window.confirm(`¿Restaurar la versión ${v.number}? Se guardará antes una copia del estado actual.`)) return;
    try { await api.post(`/admin/plan-reviews/${r.id}/versions/${v.id}/restore`); toast.success("Versión restaurada"); onChange(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Card title="Versiones e historial" testid="pr-versions-card">
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input className="qv-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota de la versión (opcional)" style={{ flex: 1 }} data-testid="pr-version-note" />
        <button style={btn("transparent")} onClick={save} data-testid="pr-version-save"><Save size={14} /> Guardar versión</button>
      </div>
      {(r.versions || []).map((v) => (
        <div key={v.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, borderTop: "1px solid rgba(255,255,255,0.05)", padding: "8px 0", fontSize: 13, flexWrap: "wrap" }} data-testid={`pr-version-${v.number}`}>
          <span><b>v{v.number}</b> · {VERSION_KIND[v.kind] || v.kind} · {fmtDate(v.created_at)}{v.note && ` · ${v.note}`}{r.published_version_id === v.id && <span style={{ color: "#22c55e" }}> · visible para el cliente</span>}</span>
          <span style={{ display: "flex", gap: 10 }}>
            <button className="emp-linkbtn" style={{ marginTop: 0 }} onClick={() => setView(v.id)} data-testid={`pr-version-view-${v.number}`}>Ver</button>
            {!locked && <button className="emp-linkbtn" style={{ marginTop: 0 }} onClick={() => restore(v)} data-testid={`pr-version-restore-${v.number}`}>Restaurar</button>}
          </span>
        </div>
      ))}
      {(r.status_history || []).length > 0 && (
        <details style={{ marginTop: 10, fontSize: 12, color: "#a3a3a3" }} data-testid="pr-status-history">
          <summary style={{ cursor: "pointer" }}>Historial de estados ({r.status_history.length})</summary>
          {r.status_history.slice().reverse().map((h, i) => <div key={i}>{fmtDate(h.at)} · {REVIEW_STATUS[h.from]?.label || "—"} → {REVIEW_STATUS[h.to]?.label || h.to}{h.reason && ` · ${h.reason}`}</div>)}
        </details>
      )}
      <VersionViewer rid={r.id} vid={view} onClose={() => setView(null)} />
    </Card>
  );
};
